import React from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import classNames from 'classnames';
import { LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div
          className={classNames(css.content, getContentCss(numberOfColumns))}
        >
          <div className={css.detailsInfo}>
            <div className="footer-left">
              <a
                href="https://caseformaking.com/"
                target="_blank"
                className="footer-cfm"
              >
                <img
                  src="https://cdn.shopify.com/s/files/1/0631/2017/9426/files/cfm.png"
                  alt="CFM logo"
                  width="80"
                  height="109"
                />
              </a>
              <div>
                <ul class="List_ul__SLzBr">
                  <li class="List_li__u1CDk">
                    <a
                      class="Link_link__lXwyG"
                      target="_blank"
                      href="https://caseformaking.com/collections/handmade-watercolors"
                    >
                      HANDMADE WATERCOLORS
                    </a>
                  </li>
                  <li class="List_li__u1CDk">
                    <a
                      class="Link_link__lXwyG"
                      target="_blank"
                      href="https://caseformaking.com/collections/supplies"
                    >
                      CURATED SUPPLIES
                    </a>
                  </li>
                  <li class="List_li__u1CDk">
                    <a
                      class="Link_link__lXwyG"
                      target="_blank"
                      href="https://caseformaking.com/pages/workshops"
                    >
                      WORKSHOPS ONLINE & IN-PERSON
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="text-left">
              <small>© Case for Making {new Date().getFullYear()}</small>
              <br></br>
              <a
                className="lindsay"
                target="_blank"
                href="https://www.lindsaystripling.com/"
              >
                <em>Landing page icons by Lindsay Stripling</em>
              </a>
              <br></br>
              <a
                className="lindsay"
                target="_blank"
                href="https://www.zoeportfolio.com"
              >
                <em>Site photography by Zoe Ching</em>
              </a>
            </div>
          </div>
          <div className={classNames(css.grid, getGridCss(1))}>
            <div className="text-right">
              {showSocialMediaLinks ? (
                <div className={css.icons}>
                  <BlockBuilder
                    blocks={linksWithBlockId}
                    sectionId={sectionId}
                    options={options}
                  />
                </div>
              ) : null}
            </div>
            <div className="text-right footer-menu">
              <BlockBuilder
                blocks={blocks}
                sectionId={sectionId}
                options={options}
              />
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
